import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { COOKIE_CATEGORIES } from "@/lib/cookieCatalogue";
import { getConsent, visibleTikTokCookies } from "@/lib/consent";

const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });

export default function CookiePreferences({ open, onClose, onDecide }) {
  const [adv, setAdv] = useState(false);
  const [ana, setAna] = useState(false);
  const [withdrawn, setWithdrawn] = useState(null);
  const current = getConsent();

  useEffect(() => {
    if (!open) return;
    setAdv(!!current?.advertising);
    setAna(!!current?.analytics);
    setWithdrawn(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (choice) => {
    const withdrawing = !!current?.advertising && !choice.advertising;
    onDecide(choice, withdrawing);
    setAdv(!!choice.advertising); setAna(!!choice.analytics);
    if (withdrawing) setWithdrawn(visibleTikTokCookies());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#FFF9EE] border-[3px] border-[#19324A] p-0" data-testid="cookie-preferences">
        <div className="h-2 bg-[linear-gradient(90deg,#FFD93D_0%,#FF6B9D_50%,#4CC9F0_100%)] rounded-t-2xl" />
        <div className="p-5 sm:p-7 min-w-0">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display font-extrabold text-2xl text-[#19324A]">Your Cookie Preferences 🍪🐾</DialogTitle>
            <DialogDescription className="text-[#19324A]/75 text-sm leading-relaxed">
              Choose what Chewie & Solo may use. Nothing optional is pre-ticked, and you can come back and change your mind any time from the
              <strong> Cookie Settings</strong> link in the footer.
            </DialogDescription>
          </DialogHeader>

          {current && (
            <p className="mt-3 text-xs rounded-2xl bg-white border border-[#19324A]/10 px-3 py-2 text-[#19324A]/70" data-testid="cookie-current-status">
              Current choice: <strong>{current.advertising ? "advertising allowed" : "optional cookies rejected"}</strong> on {fmt(current.decided_at)} · policy {current.version} · ref {current.consent_id.slice(0, 8)}
            </p>
          )}

          <div className="mt-5 space-y-4">
            {COOKIE_CATEGORIES.map((cat) => {
              const on = cat.locked ? true : cat.key === "advertising" ? adv : ana;
              const set = cat.key === "advertising" ? setAdv : setAna;
              return (
                <section key={cat.key} className="rounded-3xl bg-white border border-[#19324A]/10 p-4 sm:p-5" data-testid={`cookie-cat-${cat.key}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display font-bold text-[#19324A] text-lg flex items-center gap-2">
                        {cat.label}{cat.locked && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide bg-[#4CC9F0]/20 text-[#19324A] rounded-full px-2 py-0.5"><Lock className="w-3 h-3" /> Always on</span>}
                      </h3>
                      <p className="text-sm text-[#19324A]/75 mt-1 leading-relaxed">{cat.blurb}</p>
                    </div>
                    <Switch checked={on} disabled={cat.locked} onCheckedChange={(v) => set(!!v)} aria-label={`${cat.label} cookies`}
                      className="mt-1 data-[state=checked]:bg-[#FF6B9D] data-[state=unchecked]:bg-[#19324A]/20" data-testid={`cookie-toggle-${cat.key}`} />
                  </div>
                  <details className="mt-3 group">
                    <summary className="cursor-pointer text-xs font-bold text-[#FF6B9D] select-none" data-testid={`cookie-details-${cat.key}`}>See the cookies in this group</summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs text-left text-[#19324A]/80">
                        <thead><tr className="text-[#19324A] border-b border-[#19324A]/10"><th className="py-1.5 pr-2">Name</th><th className="py-1.5 pr-2">Who sets it</th><th className="py-1.5 pr-2">Why</th><th className="py-1.5 pr-2">Type</th><th className="py-1.5">Lasts</th></tr></thead>
                        <tbody>{cat.items.map((r) => (
                          <tr key={r[0]} className="border-b border-[#19324A]/5 align-top"><td className="py-1.5 pr-2 font-mono text-[11px] break-words">{r[0]}</td><td className="py-1.5 pr-2">{r[1]}</td><td className="py-1.5 pr-2">{r[2]}</td><td className="py-1.5 pr-2">{r[3]}</td><td className="py-1.5">{r[4]}</td></tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </details>
                </section>
              );
            })}
          </div>

          {withdrawn && (
            <p className="mt-4 text-xs rounded-2xl bg-[#FFD93D]/30 px-3 py-2 text-[#19324A]" data-testid="cookie-withdrawn-note">
              Advertising consent withdrawn. TikTok's pixel has been switched off and {withdrawn.length ? `we couldn't remove ${withdrawn.join(", ")} (set with a scope we can't reach)` : "its cookies on this site were removed"}.
              Cookies TikTok set on tiktok.com itself can only be cleared in your browser settings or your TikTok account.
              <button type="button" onClick={onClose} className="ml-2 underline font-bold" data-testid="cookie-withdrawn-close">Done</button>
            </p>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <button type="button" onClick={() => save({ advertising: adv, analytics: ana, method: "custom" })}
              className="min-h-[48px] rounded-full bg-[#19324A] text-white font-display font-bold px-5 hover:bg-[#24466a] focus-visible:ring-4 ring-[#4CC9F0]/60 outline-none" data-testid="cookie-save">Save my choices</button>
            <button type="button" onClick={() => save({ advertising: true, analytics: true, method: "accept_all" })}
              className="min-h-[48px] rounded-full bg-[#FFD93D] text-[#19324A] font-display font-bold px-5 hover:bg-[#ffe066] focus-visible:ring-4 ring-[#4CC9F0]/60 outline-none" data-testid="cookie-prefs-accept-all">Accept All</button>
            <button type="button" onClick={() => save({ advertising: false, analytics: false, method: "reject_optional" })}
              className="min-h-[48px] rounded-full bg-[#FF6B9D] text-white font-display font-bold px-5 hover:bg-[#ff85ae] focus-visible:ring-4 ring-[#4CC9F0]/60 outline-none" data-testid="cookie-prefs-reject">Reject Optional</button>
          </div>
          <p className="mt-3 text-xs text-[#19324A]/60 text-center">
            Full details in our <Link to="/legal/cookie-policy" onClick={onClose} className="underline font-semibold">Cookie Policy</Link> · ABN 36 426 728 604
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
